// client/src/components/CarouselTemplateForm.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { inputStyle, labelStyle, sectionStyle, META_LIMITS } from './templateStyles';
import { extractVars, buildCarouselConfig } from '../utils/templateConfig';
import { useIsMobile } from '../hooks/useMediaQuery';
import VariableEditor from './VariableEditor';

const MIN_CARDS = 2;
const MAX_CARDS = 10;

function emptyCard() {
  return { mediaUrl: '', mediaName: '', uploading: false, bodyText: '', variants: {}, buttons: [] };
}

function CarouselTemplateForm({ token, channelId, language, category, name, customFields }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [bodyText, setBodyText] = useState('');
  const [bodyVariants, setBodyVariants] = useState({});
  const [mediaType, setMediaType] = useState('IMAGE');
  // buttonStructure: define tipos de botón comunes a todas las cards (máx 2)
  const [buttonStructure, setButtonStructure] = useState([]);
  const [cards, setCards] = useState([emptyCard(), emptyCard()]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bodyVars = extractVars(bodyText);

  const handleBodyChange = (text) => {
    setBodyText(text);
    const newVars = extractVars(text);
    setBodyVariants((prev) => {
      const updated = {};
      newVars.forEach((v) => { updated[v] = prev[v] || { type: 'fixed', value: '' }; });
      return updated;
    });
  };

  const updateCard = (idx, patch) =>
    setCards((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  // Al cambiar el texto de una tarjeta, mantenemos su mapa de variables sincronizado
  // con las {{variables}} detectadas y EN ORDEN de aparición (= {{1}}..{{n}}). Así el
  // envío nunca manda menos parámetros de los que la tarjeta espera. Igual que el
  // cuerpo principal (handleBodyChange).
  const handleCardBodyChange = (idx, text) => {
    const newVars = extractVars(text);
    setCards((prev) => prev.map((c, i) => {
      if (i !== idx) return c;
      const updated = {};
      newVars.forEach((v) => { updated[v] = c.variants[v] || { type: 'fixed', value: '' }; });
      return { ...c, bodyText: text, variants: updated };
    }));
  };

  const addCard = () => { if (cards.length < MAX_CARDS) setCards((prev) => [...prev, emptyCard()]); };
  const removeCard = (idx) => { if (cards.length > MIN_CARDS) setCards((prev) => prev.filter((_, i) => i !== idx)); };

  const addStructButton = () => {
    if (buttonStructure.length >= 2) return;
    setButtonStructure((prev) => [...prev, { type: 'QUICK_REPLY' }]);
  };
  const removeStructButton = (i) => {
    setButtonStructure((prev) => prev.filter((_, j) => j !== i));
    // Quita esa posición en todas las cards para no dejar valores desalineados.
    setCards((prev) => prev.map((c) => ({ ...c, buttons: c.buttons.filter((_, j) => j !== i) })));
  };
  const updateStructButton = (i, type) => {
    setButtonStructure((prev) => prev.map((b, j) => (j === i ? { type } : b)));
    // Al cambiar el tipo, limpia los valores previos de esa posición en cada card.
    setCards((prev) => prev.map((c) => {
      const btns = [...c.buttons];
      btns[i] = {};
      return { ...c, buttons: btns };
    }));
  };

  const updateCardButton = (cardIdx, btnIdx, field, value) =>
    setCards((prev) => prev.map((c, i) => {
      if (i !== cardIdx) return c;
      const btns = [...c.buttons];
      btns[btnIdx] = { ...(btns[btnIdx] || {}), [field]: value };
      return { ...c, buttons: btns };
    }));

  const uploadCardMedia = async (idx, file) => {
    updateCard(idx, { uploading: true });
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/templates/upload-media', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      updateCard(idx, { mediaUrl: data.url, mediaName: file.name, uploading: false });
    } catch (err) {
      setError(`Error al subir archivo: ${err.message}`);
      updateCard(idx, { uploading: false });
    }
  };

  const validate = () => {
    if (!channelId) return 'Selecciona un canal';
    if (!name.trim()) return 'El nombre es requerido';
    if (!bodyText.trim()) return 'El cuerpo principal es requerido';
    if (cards.length < MIN_CARDS) return 'El carrusel necesita al menos 2 tarjetas';
    for (let i = 0; i < cards.length; i++) {
      const c = cards[i];
      if (!c.mediaUrl) return `La tarjeta ${i + 1} necesita una imagen o video`;
      if (!c.bodyText.trim()) return `La tarjeta ${i + 1} necesita texto`;
      for (let b = 0; b < buttonStructure.length; b++) {
        const filled = c.buttons[b] || {};
        const t = buttonStructure[b].type;
        if (!filled.text || !filled.text.trim()) return `Falta el texto del botón ${b + 1} en la tarjeta ${i + 1}`;
        if (t === 'URL' && !filled.url) return `Falta la URL del botón ${b + 1} en la tarjeta ${i + 1}`;
        if (t === 'PHONE_NUMBER' && !filled.phone) return `Falta el teléfono del botón ${b + 1} en la tarjeta ${i + 1}`;
      }
    }
    return '';
  };

  const buildVariants = () => ({
    body: bodyVariants,
    cards: cards.map((c) => c.variants),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) return setError(msg);
    setError('');
    setLoading(true);
    try {
      const config = buildCarouselConfig({
        bodyText, bodyVariants, mediaType, buttonStructure, cards, origin: window.location.origin,
      });
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), language, category, channel_id: Number(channelId), config, variants: buildVariants() }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.message || 'Error al guardar la plantilla');
      navigate('/plantillas', { state: { created: true } });
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const typeBtn = (label, value, current, setter) => (
    <button key={value} type="button" onClick={() => setter(value)} style={{
      padding: '6px 14px', borderRadius: '6px',
      border: `1px solid ${current === value ? theme.colors.primary : theme.colors.border}`,
      backgroundColor: current === value ? theme.colors.primary : 'transparent',
      color: current === value ? theme.colors.background : theme.colors.text,
      cursor: 'pointer', fontSize: '0.82rem', fontWeight: current === value ? '700' : '400',
    }}>{label}</button>
  );

  return (
    <form onSubmit={handleSubmit}>
      {/* Cuerpo principal */}
      <div style={sectionStyle}>
        <p style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem', margin: '0 0 1rem' }}>
          MENSAJE PRINCIPAL (obligatorio)
        </p>
        <div style={{ background: theme.colors.glowSubtle, border: `1px solid ${theme.colors.border}`, borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.82rem', lineHeight: 1.6, color: theme.colors.textMuted }}>
          💡 Para usar variables escríbelas entre <strong style={{ color: theme.colors.primary }}>llaves dobles</strong>, ej. <code style={{ color: theme.colors.primary }}>{'Hola {{nombre}}'}</code>. Puedes poner varias. <strong>No uses corchetes</strong> <code>{'[nombre]'}</code> — Meta no las reconoce.
        </div>
        <textarea value={bodyText} onChange={(e) => handleBodyChange(e.target.value)}
          placeholder="Texto que aparece arriba del carrusel. Usa {{nombre}} si necesitas." rows={3}
          maxLength={META_LIMITS.body}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6' }} />
        <VariableEditor vars={bodyVars} variants={bodyVariants} customFields={customFields} onChange={setBodyVariants} idPrefix="carbody" />
      </div>

      {/* Configuración global de cards */}
      <div style={sectionStyle}>
        <p style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem', margin: '0 0 1rem' }}>
          CONFIGURACIÓN DE LAS TARJETAS
        </p>
        <label style={labelStyle}>Tipo de media (igual para todas)</label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          {[['Imagen', 'IMAGE'], ['Video', 'VIDEO']].map(([l, v]) => typeBtn(l, v, mediaType, setMediaType))}
        </div>
        <label style={labelStyle}>Botones (mismos para todas las tarjetas, máx. 2)</label>
        {buttonStructure.map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
            <select value={b.type} onChange={(e) => updateStructButton(i, e.target.value)}
              style={{ ...inputStyle, width: isMobile ? 'auto' : '200px', flex: isMobile ? 1 : 'none', padding: '5px 8px', cursor: 'pointer' }}>
              <option value="QUICK_REPLY">Respuesta rápida</option>
              <option value="URL">Ir a URL</option>
              <option value="PHONE_NUMBER">Llamar teléfono</option>
            </select>
            <button type="button" onClick={() => removeStructButton(i)}
              style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
          </div>
        ))}
        {buttonStructure.length < 2 && (
          <button type="button" onClick={addStructButton} style={{
            padding: '6px 14px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
            borderRadius: '7px', color: theme.colors.text, cursor: 'pointer', fontSize: '0.82rem',
          }}>+ Agregar botón</button>
        )}
      </div>

      {/* Cards */}
      {cards.map((card, idx) => {
        const cardVars = extractVars(card.bodyText);
        return (
          <div key={idx} style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ color: theme.colors.primary, fontWeight: '700', fontSize: '0.9rem' }}>TARJETA {idx + 1}</span>
              <button type="button" onClick={() => removeCard(idx)} disabled={cards.length <= MIN_CARDS}
                style={{
                  background: 'none', border: 'none',
                  color: cards.length <= MIN_CARDS ? theme.colors.border : theme.colors.error,
                  cursor: cards.length <= MIN_CARDS ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                }}>Quitar</button>
            </div>

            {/* Media */}
            <input type="file" id={`card-media-${idx}`} style={{ display: 'none' }}
              accept={mediaType === 'IMAGE' ? 'image/*' : 'video/*'}
              onChange={(e) => { if (e.target.files[0]) uploadCardMedia(idx, e.target.files[0]); }} />
            <label htmlFor={`card-media-${idx}`} style={{
              display: 'inline-block', padding: '8px 18px', backgroundColor: 'transparent',
              border: `1px solid ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary,
              cursor: card.uploading ? 'not-allowed' : 'pointer', fontSize: '0.88rem', marginBottom: '0.4rem',
            }}>
              {card.uploading ? 'Subiendo...' : card.mediaName || `Seleccionar ${mediaType === 'IMAGE' ? 'imagen' : 'video'}`}
            </label>
            {card.mediaUrl && <p style={{ color: theme.colors.success, fontSize: '0.8rem', margin: '0 0 0.8rem' }}>✓ {card.mediaName}</p>}

            {/* Cuerpo de la card */}
            <label style={labelStyle}>Texto de la tarjeta</label>
            {idx === 0 && (
              <div style={{ background: theme.colors.glowSubtle, border: `1px solid ${theme.colors.border}`, borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.82rem', lineHeight: 1.6, color: theme.colors.textMuted }}>
                💡 Para usar variables en las tarjetas escríbelas entre <strong style={{ color: theme.colors.primary }}>llaves dobles</strong>, ej. <code style={{ color: theme.colors.primary }}>{'Hola {{nombre}}'}</code>. Puedes poner varias. <strong>No uses corchetes</strong> <code>{'[nombre]'}</code> — Meta no las reconoce.
              </div>
            )}
            <textarea value={card.bodyText} onChange={(e) => handleCardBodyChange(idx, e.target.value)}
              placeholder="Texto de la tarjeta. Usa {{nombre}} si necesitas." rows={2}
              maxLength={META_LIMITS.cardBody}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' }} />
            <VariableEditor vars={cardVars} variants={card.variants} customFields={customFields}
              onChange={(next) => updateCard(idx, { variants: next })} idPrefix={`card${idx}`} />

            {/* Botones de la card (según estructura global) */}
            {buttonStructure.map((struct, b) => {
              const filled = card.buttons[b] || {};
              return (
                <div key={b} style={{ marginTop: '0.8rem', padding: '0.6rem 0.8rem', backgroundColor: theme.colors.surface, borderRadius: '7px' }}>
                  <label style={labelStyle}>
                    Botón {b + 1} · {struct.type === 'QUICK_REPLY' ? 'Respuesta rápida' : struct.type === 'URL' ? 'URL' : 'Teléfono'}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input type="text" value={filled.text || ''} onChange={(e) => updateCardButton(idx, b, 'text', e.target.value)}
                      placeholder="Texto visible" maxLength={META_LIMITS.buttonText} style={{ ...inputStyle, flex: 1, minWidth: '120px', padding: '5px 8px' }} />
                    {struct.type === 'QUICK_REPLY' && (
                      <input type="text" value={filled.payload || ''} onChange={(e) => updateCardButton(idx, b, 'payload', e.target.value)}
                        placeholder="ID / palabra clave (oculto)" maxLength={META_LIMITS.quickReplyPayload} style={{ ...inputStyle, flex: 1, minWidth: '120px', padding: '5px 8px' }} />
                    )}
                    {struct.type === 'URL' && (
                      <input type="url" value={filled.url || ''} onChange={(e) => updateCardButton(idx, b, 'url', e.target.value)}
                        placeholder="https://..." maxLength={META_LIMITS.url} style={{ ...inputStyle, flex: 1, minWidth: '120px', padding: '5px 8px' }} />
                    )}
                    {struct.type === 'PHONE_NUMBER' && (
                      <input type="tel" value={filled.phone || ''} onChange={(e) => updateCardButton(idx, b, 'phone', e.target.value)}
                        placeholder="+57..." maxLength={META_LIMITS.phone} style={{ ...inputStyle, flex: 1, minWidth: '120px', padding: '5px 8px' }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Agregar card */}
      <div style={{ marginBottom: '1rem' }}>
        <button type="button" onClick={addCard} disabled={cards.length >= MAX_CARDS} style={{
          padding: '8px 18px', backgroundColor: 'transparent',
          border: `1px solid ${cards.length >= MAX_CARDS ? theme.colors.border : theme.colors.primary}`,
          borderRadius: '7px', color: cards.length >= MAX_CARDS ? theme.colors.textMuted : theme.colors.primary,
          cursor: cards.length >= MAX_CARDS ? 'not-allowed' : 'pointer', fontSize: '0.88rem',
        }}>
          {cards.length >= MAX_CARDS ? 'Máximo 10 tarjetas' : '+ Agregar tarjeta'}
        </button>
        <span style={{ color: theme.colors.textMuted, fontSize: '0.8rem', marginLeft: '0.8rem' }}>{cards.length}/{MAX_CARDS}</span>
      </div>

      {error && <p style={{ color: theme.colors.error, textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}

      <button type="submit" disabled={loading} style={{
        width: '100%', padding: '12px', backgroundColor: loading ? theme.colors.border : theme.colors.primary,
        color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '1rem',
        fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
      }}>{loading ? 'Guardando...' : 'Guardar carrusel'}</button>
    </form>
  );
}

export default CarouselTemplateForm;
